import { Icon, IconButton, tokens } from "@adasupport/byron";
import React, { type ReactNode, useEffect } from "react";
import { Transition } from "react-transition-group";

import * as S from "./styles";

export enum FlyoutPanelWidths {
  Small = 675,
  Large = 1030,
}

const Root = ({
  isShown,
  children,
  width = FlyoutPanelWidths.Small,
}: {
  isShown: boolean;
  children: ReactNode;
  width?: FlyoutPanelWidths;
}) => (
  <Transition in={isShown} timeout={200} mountOnEnter unmountOnExit>
    {(state) => (
      <S.FlyoutPanel>
        <S.FlyoutWindow width={width} state={state}>
          {children}
        </S.FlyoutWindow>
      </S.FlyoutPanel>
    )}
  </Transition>
);

const Header = ({
  onClose,
  children,
}: {
  onClose: () => void;
  children: ReactNode;
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div>
      {children}
      <S.CloseButton>
        <IconButton
          icon={Icon.Close}
          aria-label="Close"
          onClick={onClose}
          variant="tertiary"
          iconColor={tokens.colors.text.muted}
        />
      </S.CloseButton>
    </div>
  );
};

const Content = ({ children }: { children: ReactNode }) => (
  <S.FlyoutContent>{children}</S.FlyoutContent>
);

const Overlay = ({
  isShown = false,
  onClick,
}: {
  isShown: boolean;
  onClick: () => void;
}) => (
  <Transition in={isShown} timeout={200} mountOnEnter unmountOnExit>
    {(state) => (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: tokens.colors.background.mainInverted,
          zIndex: 1,
          transition: "opacity 200ms ease-in-out",
          opacity: state === "entered" || state === "entering" ? 0.2 : 0,
        }}
        onClick={onClick}
      />
    )}
  </Transition>
);

export const FlyoutPanel = {
  Root,
  Header,
  Content,
  Overlay,
};
